import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  BookOpen,
  Building2,
  ChartColumn,
  Check,
  LucideIcon,
  Network,
  Target,
  TrendingUp,
  Users,
  Waypoints,
  Workflow,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Brand } from '../components/dashboard/Brand';
import { Button, Card } from '../components/ui';
import { AUTH_BUTTON, AuthActions, AuthScreen, StepBar } from '../components/auth/AuthShell';

interface OnboardingItem {
  /** Defaults to a check mark */
  icon?: LucideIcon;
  label: string;
  meta?: string;
}

interface OnboardingStep {
  title: string;
  meta: string;
  items: OnboardingItem[];
}

const STEPS: OnboardingStep[] = [
  {
    title: 'Welcome',
    meta: 'Stakeholder intelligence for PR teams',
    items: [
      { icon: Target, label: 'Strategic intelligence', meta: 'Data-driven decisions' },
      { icon: TrendingUp, label: 'PR excellence', meta: 'Built for PR teams' },
      { icon: Users, label: 'Team collaboration', meta: 'Shared workflows' },
    ],
  },
  {
    title: 'Core features',
    meta: 'Everything for stakeholder relationships',
    items: [
      { icon: Network, label: 'Stakeholder intelligence', meta: 'Map your network' },
      { icon: Building2, label: 'Organization mapping', meta: 'Structures and hierarchies' },
      { icon: Waypoints, label: 'Influence analytics', meta: 'Patterns and strategy' },
      { icon: BookOpen, label: 'Knowledge hub', meta: 'Context in one place' },
      { icon: Workflow, label: 'Strategic workflows', meta: 'Streamlined engagement' },
      { icon: ChartColumn, label: 'Performance intelligence', meta: 'Engagement and ROI' },
    ],
  },
  {
    title: "You're all set",
    meta: 'Next, create the administrator account',
    items: [
      { label: 'Stakeholder intelligence' },
      { label: 'Team management' },
      { label: 'System configuration' },
      { label: 'Influence analytics' },
      { label: 'Workflow design' },
      { label: 'Advanced reporting' },
    ],
  },
];

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { requiresSetup, setupLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  // Redirect to login if setup is already complete
  if (!setupLoading && !requiresSetup) {
    return <Navigate to="/login" replace />;
  }

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as seen and navigate to setup
      console.log('Get Started clicked - navigating to /setup');
      localStorage.setItem('hasSeenOnboarding', 'true');
      // Use replace instead of navigate to prevent back button issues
      navigate('/setup', { replace: true });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    // Mark onboarding as seen and navigate to setup
    localStorage.setItem('hasSeenOnboarding', 'true');
    // Use replace instead of navigate to prevent back button issues
    navigate('/setup', { replace: true });
  };

  return (
    <AuthScreen
      top={
        <div className="flex h-8 items-center justify-between">
          <Brand />
          {!isLast && (
            <Button variant="ghost" size="sm" onClick={skipOnboarding} className="-mr-2">
              Skip
            </Button>
          )}
        </div>
      }
      progress={<StepBar current={currentStep} total={STEPS.length} />}
      title={step.title}
      meta={step.meta}
    >
      <Card key={currentStep} className="animate-fade-up">
        <ul className="divide-y divide-neutral-200/70">
          {step.items.map(({ icon: Icon = Check, label, meta }) => (
            <li key={label} className="flex items-center gap-3 px-4 py-2.5">
              <Icon className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} aria-hidden="true" />
              <div className="min-w-0">
                <p className="truncate text-[13px] text-ink">{label}</p>
                {meta && <p className="truncate text-[12px] text-neutral-400">{meta}</p>}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <AuthActions>
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button variant="secondary" onClick={prevStep} className="h-10 flex-shrink-0 sm:h-9">
              Back
            </Button>
          )}
          <Button variant="primary" onClick={nextStep} className={`${AUTH_BUTTON} flex-1`}>
            {isLast ? 'Get started' : 'Next'}
          </Button>
        </div>
      </AuthActions>
    </AuthScreen>
  );
};

export default OnboardingPage;
